import React from "react";
import { APP_CONFIG } from "../../config/appConfig";
import "./FactoryScene.css";

const lamps = Array.from({ length: 7 });
const capsules = Array.from({ length: 6 });
const dust = Array.from({ length: 28 });
const sparks = Array.from({ length: 12 });

function IndicatorPanel({ side }) {
    return (
        <div className={`indicator-panel indicator-panel--${side}`} aria-hidden="true">
            <div className="indicator-panel__top"><i /><i /><i /></div>
            <div className="indicator-panel__meter"><span /></div>
            <div className="indicator-panel__slots"><i /><i /><i /><i /><i /></div>
        </div>
    );
}

function FanUnit({ side }) {
    return (
        <div className={`fan-unit fan-unit--${side}`} aria-hidden="true">
            <div className="fan-unit__housing">
                <div className="fan-unit__rotor">
                    <i /><i /><i /><i /><i /><i />
                    <span />
                </div>
            </div>
            <div className="fan-unit__grille" />
        </div>
    );
}

function Tank({ side }) {
    return (
        <div className={`factory-tank factory-tank--${side}`} aria-hidden="true">
            <div className="factory-tank__cap" />
            <div className="factory-tank__neck" />
            <div className="factory-tank__body">
                <div className="factory-tank__window">
                    <div className="factory-tank__liquid" />
                    <div className="factory-tank__bubbles"><i /><i /><i /><i /></div>
                </div>
                <div className="factory-tank__bands"><i /><i /><i /></div>
                <div className="factory-tank__gauge"><span /></div>
            </div>
            <div className="factory-tank__feet"><i /><i /></div>
            <div className="factory-tank__pipe" />
        </div>
    );
}

function PistonMachine({ side }) {
    return (
        <div className={`piston-machine piston-machine--${side}`} aria-hidden="true">
            <div className="piston-machine__body">
                <div className="piston-machine__lamp" />
                <div className="piston-machine__vent" />
            </div>
            <div className="piston-machine__rail">
                <div className="piston-machine__rod" />
                <div className="piston-machine__head" />
            </div>
        </div>
    );
}

function HeartCore({ heartbeatToken }) {
    return (
        <div className="foundry-core" aria-hidden="true">
            <div className="foundry-core__cables foundry-core__cables--left"><i /><i /><i /></div>
            <div className="foundry-core__cables foundry-core__cables--right"><i /><i /><i /></div>
            <div className="foundry-core__brace foundry-core__brace--left" />
            <div className="foundry-core__brace foundry-core__brace--right" />
            <div className="foundry-core__shell">
                <div className="foundry-core__ring foundry-core__ring--outer"><i /><i /><i /><i /></div>
                <div className="foundry-core__ring foundry-core__ring--mid"><i /><i /><i /><i /><i /><i /></div>
                <div className="foundry-core__ring foundry-core__ring--inner"><i /><i /><i /></div>
                <div className="foundry-core__glass">
                    <div className="foundry-core__energy" />
                    <div className="foundry-core__heart" key={`core-heart-${heartbeatToken}`}><span /></div>
                    <div className="foundry-core__arc foundry-core__arc--a" />
                    <div className="foundry-core__arc foundry-core__arc--b" />
                    <div className="foundry-core__arc foundry-core__arc--c" />
                </div>
            </div>
            <div className="foundry-core__pedestal">
                <i /><i /><i />
            </div>
        </div>
    );
}

function ProductionLine() {
    return (
        <div className="production-line" aria-hidden="true">
            <div className="production-line__machine production-line__machine--charge">
                <div className="production-line__nozzle" />
                <div className="production-line__beam" />
            </div>
            <div className="production-line__machine production-line__machine--form">
                <div className="production-line__coil"><i /><i /><i /></div>
                <div className="production-line__field" />
            </div>
            <div className="production-line__machine production-line__machine--seal">
                <div className="production-line__scanner" />
                <div className="production-line__scan" />
            </div>
            <div className="factory-conveyor">
                <div className="factory-conveyor__rear-rail" />
                <div className="factory-conveyor__belt">
                    <div className="factory-conveyor__slats" />
                    {capsules.map((_, index) => (
                        <div className="heart-capsule" key={index} style={{ "--capsule-index": index }}>
                            <div className="heart-capsule__shell">
                                <div className="heart-capsule__energy" />
                                <div className="heart-capsule__heart"><span /></div>
                                <div className="heart-capsule__shine" />
                            </div>
                        </div>
                    ))}
                </div>
                <div className="factory-conveyor__front-rail" />
                <div className="factory-conveyor__legs"><i /><i /><i /><i /></div>
            </div>
        </div>
    );
}

export default function FactoryScene({ phase, stage, heartbeatToken, finaleToken }) {
    const alive = phase === "activating" || phase === "active";

    return (
        <div className={`factory-scene factory-scene--${phase} stage-${stage}`}>
            <div className="factory-world">
                <div className="factory-far-depth" aria-hidden="true">
                    <div className="factory-far-depth__wall" />
                    <div className="factory-far-depth__door factory-far-depth__door--left" />
                    <div className="factory-far-depth__door factory-far-depth__door--right" />
                    <div className="factory-far-depth__tower factory-far-depth__tower--a" />
                    <div className="factory-far-depth__tower factory-far-depth__tower--b" />
                    <div className="factory-far-depth__tower factory-far-depth__tower--c" />
                </div>

                <div className="factory-backwall">
                    <div className="factory-backwall__panels" />
                    <div className="factory-backwall__rib factory-backwall__rib--a" />
                    <div className="factory-backwall__rib factory-backwall__rib--b" />
                    <div className="factory-sign">
                        <span>HEART FOUNDRY</span>
                        <small>UNIT 01</small>
                    </div>
                </div>

                <div className="factory-catwalk" aria-hidden="true">
                    <div className="factory-catwalk__deck" />
                    <div className="factory-catwalk__rail"><i /><i /><i /><i /><i /><i /><i /></div>
                    <div className="factory-catwalk__stairs factory-catwalk__stairs--left" />
                    <div className="factory-catwalk__stairs factory-catwalk__stairs--right" />
                </div>

                <div className="factory-ceiling" aria-hidden="true">
                    <div className="factory-ceiling__beam factory-ceiling__beam--a" />
                    <div className="factory-ceiling__beam factory-ceiling__beam--b" />
                    <div className="factory-ceiling__beam factory-ceiling__beam--c" />
                    <div className="factory-ceiling__crossbars">
                        {lamps.map((_, index) => <i key={index} />)}
                    </div>
                </div>

                <div className="factory-lamps" aria-hidden="true">
                    {lamps.map((_, index) => (
                        <div className="factory-lamp" key={index} style={{ "--lamp": index }}>
                            <span />
                            <div className="factory-lamp__cone" />
                        </div>
                    ))}
                </div>

                <svg className="factory-pipe-network" viewBox="0 0 1600 900" preserveAspectRatio="none" aria-hidden="true">
                    <g className="pipe-shadow">
                        <path d="M-40 195 H335 Q405 195 405 265 V395 H590" />
                        <path d="M1640 185 H1270 Q1195 185 1195 255 V395 H1010" />
                        <path d="M115 -30 V285 Q115 350 185 350 H460" />
                        <path d="M1485 -30 V300 Q1485 365 1410 365 H1135" />
                        <path d="M-40 605 H300 Q360 605 360 550 V515 H500" />
                        <path d="M1640 610 H1300 Q1240 610 1240 550 V515 H1100" />
                    </g>
                    <g className="pipe-shells">
                        <path d="M-40 195 H335 Q405 195 405 265 V395 H590" />
                        <path d="M1640 185 H1270 Q1195 185 1195 255 V395 H1010" />
                        <path d="M115 -30 V285 Q115 350 185 350 H460" />
                        <path d="M1485 -30 V300 Q1485 365 1410 365 H1135" />
                        <path d="M-40 605 H300 Q360 605 360 550 V515 H500" />
                        <path d="M1640 610 H1300 Q1240 610 1240 550 V515 H1100" />
                    </g>
                    <g className="pipe-highlight">
                        <path d="M-40 195 H335 Q405 195 405 265 V395 H590" />
                        <path d="M1640 185 H1270 Q1195 185 1195 255 V395 H1010" />
                        <path d="M115 -30 V285 Q115 350 185 350 H460" />
                        <path d="M1485 -30 V300 Q1485 365 1410 365 H1135" />
                    </g>
                    <g className="pipe-energy">
                        <path d="M-40 195 H335 Q405 195 405 265 V395 H590" />
                        <path d="M1640 185 H1270 Q1195 185 1195 255 V395 H1010" />
                        <path d="M115 -30 V285 Q115 350 185 350 H460" />
                        <path d="M1485 -30 V300 Q1485 365 1410 365 H1135" />
                        <path d="M-40 605 H300 Q360 605 360 550 V515 H500" />
                        <path d="M1640 610 H1300 Q1240 610 1240 550 V515 H1100" />
                    </g>
                </svg>

                <div className="factory-manifold factory-manifold--left" aria-hidden="true"><i /><i /><i /><span /></div>
                <div className="factory-manifold factory-manifold--right" aria-hidden="true"><i /><i /><i /><span /></div>

                <div className="factory-columns" aria-hidden="true">
                    <i className="factory-column factory-column--l1" />
                    <i className="factory-column factory-column--l2" />
                    <i className="factory-column factory-column--r1" />
                    <i className="factory-column factory-column--r2" />
                </div>

                <Tank side="left" />
                <Tank side="right" />
                <FanUnit side="left" />
                <FanUnit side="right" />
                <PistonMachine side="left" />
                <PistonMachine side="right" />
                <IndicatorPanel side="left" />
                <IndicatorPanel side="right" />

                <div className="foundry-frame" aria-hidden="true">
                    <div className="foundry-frame__arch" />
                    <div className="foundry-frame__rail foundry-frame__rail--left" />
                    <div className="foundry-frame__rail foundry-frame__rail--right" />
                    <div className="foundry-frame__header"><i /><i /><i /><i /><i /></div>
                    <HeartCore heartbeatToken={heartbeatToken} />
                    <div className="foundry-frame__status"><i /><i /><i /><i /><i /></div>
                </div>

                <div className="factory-arm factory-arm--left" aria-hidden="true">
                    <div className="factory-arm__shoulder" />
                    <div className="factory-arm__upper" />
                    <div className="factory-arm__joint" />
                    <div className="factory-arm__lower" />
                    <div className="factory-arm__tool" />
                </div>
                <div className="factory-arm factory-arm--right" aria-hidden="true">
                    <div className="factory-arm__shoulder" />
                    <div className="factory-arm__upper" />
                    <div className="factory-arm__joint" />
                    <div className="factory-arm__lower" />
                    <div className="factory-arm__tool" />
                </div>

                <ProductionLine />

                <div className="factory-floor">
                    <div className="factory-floor__grid" />
                    <div className="factory-floor__track factory-floor__track--left" />
                    <div className="factory-floor__track factory-floor__track--right" />
                    <div className="factory-floor__reflection" />
                    <div className="factory-floor__core-reflection" />
                </div>

                <div className="factory-steam factory-steam--left" aria-hidden="true"><i /><i /><i /></div>
                <div className="factory-steam factory-steam--right" aria-hidden="true"><i /><i /><i /></div>

                <div className="factory-sparks factory-sparks--left" aria-hidden="true">
                    {sparks.slice(0, 6).map((_, index) => <i key={index} style={{ "--spark": index }} />)}
                </div>
                <div className="factory-sparks factory-sparks--right" aria-hidden="true">
                    {sparks.slice(6).map((_, index) => <i key={index} style={{ "--spark": index }} />)}
                </div>

                <div className="factory-dust" aria-hidden="true">
                    {dust.map((_, index) => (
                        <i
                            key={index}
                            style={{
                                "--dust": index,
                                left: `${(index * 37 + 11) % 97}%`,
                                top: `${(index * 53 + 7) % 88}%`,
                                animationDuration: `${5.1 + (index % 5) * 0.74}s`
                            }}
                        />
                    ))}
                </div>
            </div>

            <div className="factory-atmosphere" aria-hidden="true" />
            <div className="factory-darkness" aria-hidden="true" />
            <div className="factory-emergency-light factory-emergency-light--left" aria-hidden="true" />
            <div className="factory-emergency-light factory-emergency-light--right" aria-hidden="true" />
            <div className="factory-vignette" aria-hidden="true" />

            {alive && stage === 2 && <div className="factory-power-wave factory-power-wave--startup" />}
            {stage === 7 && <div className="factory-final-sync" key={`final-${finaleToken}`} />}
            {heartbeatToken > 0 && (
                <React.Fragment key={`beat-${heartbeatToken}`}>
                    <div className="factory-heartbeat-wave" />
                    <div className="factory-heartbeat-flash" />
                    <svg className="factory-heartbeat-pipes" viewBox="0 0 1600 900" preserveAspectRatio="none" aria-hidden="true">
                        <path d="M-40 195 H335 Q405 195 405 265 V395 H590" />
                        <path d="M1640 185 H1270 Q1195 185 1195 255 V395 H1010" />
                        <path d="M115 -30 V285 Q115 350 185 350 H460" />
                        <path d="M1485 -30 V300 Q1485 365 1410 365 H1135" />
                    </svg>
                </React.Fragment>
            )}

            {phase === "active" && (
                <div className="factory-final-message">
                    <span>{APP_CONFIG.finalMessage}</span>
                </div>
            )}
        </div>
    );
}
